/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import renderUnsupportedMediaDialog from '../../components/UnsupportedMediaDialog/renderUnsupportedMediaDialog';
import addProduct from '../actions/assets/addProduct';
import TYPES from '../action-types';
import getAggregatedData from '../../helpers/assets/getAggregatedData';
import {
  appendMasterGalleryAssets,
  applyAppendMasterGalleryAssetsToState,
  buildOptimisticMasterAssets,
  resolveAssetsFromState,
} from './helpers/appendMasterGalleryAssets';
import { showMediaAddFailedToast, showMediaAddedToast } from './helpers/showMediaAddedToast';
import { fetchAssets } from './fetchAssets';

const resolveProduct = (product, getState) => {
  if (product?.title) return product;

  const fromStore = getState().products?.items?.find(({ _id }) => _id === product?._id);
  return fromStore || product;
};

const buildMasterProductLink = (product) => ({
  _id: product._id,
  title: product.title || product._id,
  type: 'master',
});

const getExistingMasterAssets = (state, productId) => (
  state.products?.entities?.assets?.masterAssets?.[productId]
    || state.products?.items?.find((item) => item._id === productId)?.assets
    || []
);

export const addAssets = createAsyncThunk(
  'products/addAssets',
  async ({ assetIds, product, skipSuccessToast = false }, {
    dispatch,
    extra: {
      sdk, Logger, utils, showErrorDialog, localization: l10n, Toast,
    },
    getState,
  }) => {
    try {
      const ids = assetIds || getState().assets?.selectedAssetsIds;
      const resolvedProduct = resolveProduct(product, getState);

      const { data } = await sdk.assets.attachToProduct(resolvedProduct._id, ids);

      let assetsWithReason = { failedIds: [], codes: {} };
      const skipped = data.skipped || data.assetsWithReason || [];
      if (skipped.length) {
        assetsWithReason = skipped.reduce((acc, { _id, reason, name }) => {
          acc.failedIds.push(_id);
          acc.codes[reason] = acc.codes[reason] ? [...acc.codes[reason], name] : [name];
          return acc;
        }, { failedIds: [], codes: {} });
      }

      const isAsync = Boolean(data.assetsToProcessCount);
      const successfulIds = assetsWithReason.failedIds.length
        ? ids.filter((id) => !assetsWithReason.failedIds.includes(id))
        : ids;
      const assetsToProcessCount = data.assetsToProcessCount ?? data.attachedCount ?? 0;

      if (Object.keys(assetsWithReason.codes).length !== 0) {
        renderUnsupportedMediaDialog({
          codes: assetsWithReason.codes,
          allowedAssetCount: assetsToProcessCount,
          allAssetCount: ids.length,
        });
      }

      if (isAsync && assetsToProcessCount > 0) {
        Toast(l10n.TOAST.ATTACHING_ASSETS_TO_PRODUCT_SENT);
      }

      if (!isAsync && successfulIds.length) {
        const masterProductLink = buildMasterProductLink(resolvedProduct);
        const state = getState();
        const existingMasterAssets = getExistingMasterAssets(state, resolvedProduct._id);
        const resolvedAssets = resolveAssetsFromState(state, successfulIds);
        const optimisticAssets = buildOptimisticMasterAssets({
          assets: resolvedAssets,
          product: resolvedProduct,
          existingMasterAssets,
        });

        successfulIds.forEach((assetId) => {
          dispatch(addProduct({ assetId, product: masterProductLink, productAttaching: null }));
        });

        if (optimisticAssets.length) {
          dispatch(appendMasterGalleryAssets({
            productId: resolvedProduct._id,
            assets: optimisticAssets,
          }));
        }

        await dispatch(fetchAssets({
          productId: resolvedProduct._id,
          mergeAssets: optimisticAssets,
        }));

        if (!skipSuccessToast) {
          showMediaAddedToast(Toast, { products: 1, variants: 0 });
        }

        const { assets: { selectedItems, selectedAssetsAggregatedData }, user } = getState();
        const selectedSuccessfulIds = successfulIds.filter((id) => selectedItems.includes(id));
        const hasProductInAggregated = selectedAssetsAggregatedData?.products?.some(
          ({ _id }) => String(_id) === String(resolvedProduct._id),
        );

        if (selectedSuccessfulIds.length && !hasProductInAggregated) {
          const aggregatedFromApi = await getAggregatedData(selectedSuccessfulIds, user);
          if (Object.keys(aggregatedFromApi).length) {
            dispatch({
              type: TYPES.ASSETS.CREATE_COLLECTION.COMPLETE,
              payload: {
                aggregatedData: {
                  ...selectedAssetsAggregatedData,
                  ...aggregatedFromApi,
                },
              },
            });
          }
        }

        return {
          assetIds: successfulIds,
          product: resolvedProduct,
          isAsync,
          optimisticAssets,
        };
      }

      return {
        assetIds: successfulIds, product: resolvedProduct, isAsync, optimisticAssets: [],
      };
    } catch (error) {
      const errorMessage = utils.getDataFromResponceError(error, 'msg') || 'Can not add assets to product';
      showMediaAddFailedToast(Toast);
      showErrorDialog(errorMessage);
      Logger.error(new Error('Error add assets to product'), { error }, [
        'AddAssetsToProductFailed',
        { errorMessage: errorMessage || 'NoMessage' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(
      appendMasterGalleryAssets,
      (state, { payload }) => {
        applyAppendMasterGalleryAssetsToState(state, payload || {});
      },
    )
    .addCase(
      addAssets.pending,
      () => {
        /** Do nothing */
      },
    )
    .addCase(
      addAssets.fulfilled,
      () => {
        /** Do nothing — optimistic gallery update runs via appendMasterGalleryAssets */
      },
    )
    .addCase(
      addAssets.rejected,
      (state, { error }) => {
        state.error = error;
      },
    );
};
